import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";
import { FileUp as FileUpIcon, Image as ImageIcon } from "@tamagui/lucide-icons";
import { FilePickerButton, ImagePickerButton, isWeb, Modal, YStack } from "@medbillai/ui";
import { generateFileName, isSupportedImageType } from "@medbillai/utils/images";
import { analyticsService } from "../../lib/analytics";
import type { UploadError, UploadFile } from "../../provider/documents";
import { CameraPickerButton } from "./components/CameraPickerButton";
import { UploadOptionCard } from "./components/UploadOptionCard";
import { openScanbot } from "./utils/open-scanbot/openScanbot";
export type UploadDocumentSheetProps = {
  open: boolean;
  onUploadError: (error: UploadError) => void;
  onOpenChange: (open: boolean) => void;
  onCameraPermissionCheckStart: () => void;
  onCameraPermissionCheckComplete: () => void;
  onCameraPermissionDenied?: () => void;
  onFilesLoaded: (files: UploadFile[]) => Promise<void>;
  cleanupBeforeUpload?: () => void;
  title?: string;
  subtitle?: string;
};
export const UploadDocumentSheet = ({
  open,
  onUploadError,
  onOpenChange,
  onCameraPermissionCheckStart,
  onCameraPermissionCheckComplete,
  onCameraPermissionDenied,
  onFilesLoaded,
  cleanupBeforeUpload,
  title = "Upload",
  subtitle
}: UploadDocumentSheetProps) => {
  const [useCamera, setUseCamera] = useState<boolean>(false);
  const openCamera = async () => {
    try {
      setUseCamera(true);
      const scannedImages = await openScanbot();
      if (scannedImages.status === "CANCELED") {
        return;
      }

      // Only proceed if we have valid images
      if (scannedImages.images.length > 0) {
        multiPhotoUpload(scannedImages.images);
      } else {
        throw new Error("No valid images found");
      }
    } catch (error) {
      console.error("Error during document scanning:", error);
      onUploadError({
        error: error instanceof Error ? error : new Error(String(error))
      });
    } finally {
      setUseCamera(false);
    }
  };
  const onError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      onUploadError({
        error
      });
      return;
    }
    onUploadError({
      error: new Error("Unknown error")
    });
  }, [onUploadError]);
  const multiPhotoUpload = useCallback((images: {
    id: string;
    path: string;
  }[]) => {
    const handleUpload = async () => {
      if (images.length === 0) {
        return;
      }
      try {
        const uploadFiles = await Promise.all(images.reverse().map(async (image, idx) => {
          const response = await fetch(image.path);
          const blob = await response.blob();
          if (!isSupportedImageType(blob.type)) {
            onUploadError({
              message: "This file type is not currently supported."
              // No error field - so we don't log an error to Sentry.
            });
            throw new Error("Unsupported file type");
          }
          const filename = `${idx}-${generateFileName(blob.type)}`;
          return {
            name: filename,
            sizeBytes: blob.size,
            contentType: blob.type,
            blob
          } as UploadFile;
        }));
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    void handleUpload();
    analyticsService.trackEvent("ClickedUpload", {
      type: "CAMERA"
    });
  }, [onError, onFilesLoaded, onUploadError, cleanupBeforeUpload]);
  const onImageSelected = (images: ImagePickerAsset[], setButtonLoading: (loading: boolean) => void): void => {
    const handleUpload = async () => {
      if (images.length === 0) {
        return;
      }
      try {
        const uploadFiles = await Promise.all(images.map(async (image, idx) => {
          const response = await fetch(image.uri);
          const blob = await response.blob();
          if (!isSupportedImageType(blob.type)) {
            onUploadError({
              message: "This file type is not currently supported."
              // No error field - so we don't log an error to Sentry.
            });
            throw new Error("Unsupported file type");
          }
          const filename = `${idx}-${generateFileName(blob.type)}`;
          return {
            name: filename,
            sizeBytes: blob.size,
            contentType: blob.type,
            blob
          } as UploadFile;
        }));
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    void handleUpload();
    setButtonLoading(false);
    analyticsService.trackEvent("ClickedUpload", {
      type: "GALLERY"
    });
  };
  const onFilesSelected = (files: DocumentPickerAsset[], setButtonLoading: (loading: boolean) => void) => {
    const handleUpload = async () => {
      if (files.length === 0) {
        return;
      }
      try {
        const uploadFiles: UploadFile[] = [];
        for (const file of files) {
          const response = await fetch(file.uri);
          const blob = await response.blob();
          uploadFiles.push({
            name: file.name,
            sizeBytes: file.size,
            contentType: blob.type,
            blob
          });
        }
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    // Close and cleanup document sheet
    void handleUpload();
    setButtonLoading(false);
    analyticsService.trackEvent("ClickedUpload", {
      type: "FILE"
    });
  };
  return <Modal open={open} onOpenChange={onOpenChange} size="small" headerProps={subtitle ? {
    title,
    variant: "largeTitle",
    subtitle
  } : {
    title
  }} data-sentry-element="Modal" data-sentry-component="UploadDocumentSheet" data-sentry-source-file="UploadDocumentSheet.tsx">
      <YStack gap="$sp1" paddingVertical={isWeb ? "$sp2" : 0} data-sentry-element="YStack" data-sentry-source-file="UploadDocumentSheet.tsx">
        {!isWeb ? <CameraPickerButton isLoading={useCamera} onPermissionCheckStart={onCameraPermissionCheckStart} onPermissionCheckComplete={onCameraPermissionCheckComplete} onPermissionDenied={onCameraPermissionDenied} setIsLoading={setUseCamera} openCamera={openCamera} /> : null}
        <ImagePickerButton onImageSelected={onImageSelected} padding={0} aria-label={isWeb ? "Upload photos" : "Choose from library"} data-sentry-element="ImagePickerButton" data-sentry-source-file="UploadDocumentSheet.tsx">
          {isLoading => <UploadOptionCard icon={ImageIcon} isLoading={isLoading} title={isWeb ? "Upload photos" : "Choose from library"} />}
        </ImagePickerButton>
        <FilePickerButton onFilesSelected={onFilesSelected} padding={0} {...isWeb ? {
        accept: ["application/pdf"],
        multiple: false
      } : {}} aria-label="Upload a file" data-sentry-element="FilePickerButton" data-sentry-source-file="UploadDocumentSheet.tsx">
          {isLoading => <UploadOptionCard icon={FileUpIcon} isLoading={isLoading} title="Upload a file" />}
        </FilePickerButton>
      </YStack>
    </Modal>;
};